// THE ONE SCREEN A FAMILY CONFIGURES (bead hf-e3b-circle-people-r3z.2).
//
// Five small forms, and one of them is dangerous. Decisions worth defending:
//
// EVERYTHING IS GATED ON `canManageSettings`, WHICH THE SERVER COMPUTED. A view-only
// relative sees the values — block boundaries are not secret, plan §11.4 — and is
// offered no controls at all, with a sentence saying why. The browser never decides
// this (AUT-007); it renders the answer it was given.
//
// THE TIMEZONE CHANGE ASKS FIRST, AND THE QUESTION EXPLAINS RATHER THAN WARNS. It is
// the only edit here that changes what every other person sees, and the honest framing
// is counter-intuitive: nothing that has been agreed moves, and yet every clock time
// on screen shifts. A bare "are you sure?" would convey neither half.
//
// FORMS ARE UNCONTROLLED, read on submit. Settings are a "type it, then save it"
// interaction, not a live-updating one; controlled state here would re-render four
// forms on every keystroke and buy nothing. The server is the validator, and its
// answers land beside the form that caused them.

import { Button, Card, Dialog, StatusMessage, TextInput } from "@herefor/design-system";
import {
  ADMISSION_ALLOWLIST_MESSAGES,
  CIRCLE_SETTINGS_MESSAGES,
  DIRECT_REQUEST_EXPIRY_POLICY_MESSAGES,
  SUPPORTED_PERSON_PRINT_CONTACT_MESSAGES,
  SUPPORTED_PERSON_NAME_MESSAGES,
} from "@herefor/product-language";
import { useState, type ReactNode, type SyntheticEvent } from "react";

import type { CircleSettingsResult } from "../../convex/circleSettings";
import type { AdmissionAllowlistResult } from "../../convex/admission";
import { BLOCK_NAMES, blockProblemMessages, messageFor } from "./settingsCopy";
import type {
  BlockDefinitionsDraft,
  CircleSettingsController,
  SettingsSection,
} from "./useCircleSettings";

const BLOCK_ORDER = ["morning", "afternoon", "evening"] as const;

export interface CircleSettingsProps {
  readonly settings: NonNullable<CircleSettingsResult>;
  readonly controller: CircleSettingsController;
  readonly actionsDisabled?: boolean;
}

type AdmissionAllowlistEntry = AdmissionAllowlistResult["entries"][number];

export type AdmissionAllowlistOutcome =
  | { readonly status: "idle" }
  | { readonly status: "working" }
  | { readonly status: "added" }
  | { readonly status: "removed" }
  | { readonly status: "invalid" }
  | { readonly status: "unavailable" };

export interface AdmissionAllowlistProps {
  readonly entries: readonly AdmissionAllowlistEntry[];
  readonly outcome: AdmissionAllowlistOutcome;
  readonly actionsDisabled?: boolean;
  readonly onAdd: (email: string) => Promise<void>;
  readonly onRemove: (entryId: AdmissionAllowlistEntry["allowlistEntryId"]) => Promise<void>;
  /** Standalone by default; level two when composed into the settings page. */
  readonly headingLevel?: 1 | 2;
}

export function CircleSettings({
  settings,
  controller,
  actionsDisabled = false,
}: CircleSettingsProps) {
  const [confirmingTimeZone, setConfirmingTimeZone] = useState<string | null>(null);
  const canManage = settings.canManageSettings;
  const busy = actionsDisabled || controller.outcome.status === "working";

  return (
    <section
      className="circle-settings"
      data-circle-settings={canManage ? "editable" : "read-only"}
    >
      <h1>{CIRCLE_SETTINGS_MESSAGES.heading}</h1>
      <p className="circle-settings__lede">{CIRCLE_SETTINGS_MESSAGES.explanation}</p>
      {canManage ? null : (
        <StatusMessage tone="info">{CIRCLE_SETTINGS_MESSAGES.readOnlyNotice}</StatusMessage>
      )}

      <Card title={CIRCLE_SETTINGS_MESSAGES.planName.legend} titleLevel={2}>
        <form
          onSubmit={(event: SyntheticEvent<HTMLFormElement>) => {
            event.preventDefault();
            const value = new FormData(event.currentTarget).get("displayName");
            void controller.savePlanName(typeof value === "string" ? value : "");
          }}
        >
          <TextInput
            defaultValue={settings.displayName}
            disabled={!canManage || busy}
            hint={CIRCLE_SETTINGS_MESSAGES.planName.hint}
            key={settings.displayName}
            label={CIRCLE_SETTINGS_MESSAGES.planName.label}
            name="displayName"
          />
          {canManage ? (
            <Button disabled={busy} type="submit" variant="secondary">
              {CIRCLE_SETTINGS_MESSAGES.planName.action}
            </Button>
          ) : null}
        </form>
        <Outcome controller={controller} section="planName" />
      </Card>

      <Card title={CIRCLE_SETTINGS_MESSAGES.blocks.legend} titleLevel={2}>
        <p className="circle-settings__hint">{CIRCLE_SETTINGS_MESSAGES.blocks.hint}</p>
        <form
          onSubmit={(event: SyntheticEvent<HTMLFormElement>) => {
            event.preventDefault();
            void controller.saveBlocks(readBlocks(new FormData(event.currentTarget)));
          }}
        >
          {BLOCK_ORDER.map((blockId) => (
            <fieldset className="circle-settings__block" key={blockId}>
              <legend>{BLOCK_NAMES[blockId]}</legend>
              <TextInput
                defaultValue={settings.blockDefinitions[blockId].startLocal}
                disabled={!canManage || busy}
                label={CIRCLE_SETTINGS_MESSAGES.blocks.startLabel}
                name={`${blockId}.startLocal`}
              />
              <TextInput
                defaultValue={settings.blockDefinitions[blockId].endLocal}
                disabled={!canManage || busy}
                label={CIRCLE_SETTINGS_MESSAGES.blocks.endLabel}
                name={`${blockId}.endLocal`}
              />
            </fieldset>
          ))}
          <p className="circle-settings__hint">{CIRCLE_SETTINGS_MESSAGES.blocks.gapsAllowed}</p>
          {canManage ? (
            <Button disabled={busy} type="submit" variant="secondary">
              {CIRCLE_SETTINGS_MESSAGES.blocks.action}
            </Button>
          ) : null}
        </form>
        <Outcome controller={controller} section="blocks" />
      </Card>

      <Card title={DIRECT_REQUEST_EXPIRY_POLICY_MESSAGES.settingsLegend} titleLevel={2}>
        <p className="circle-settings__hint">
          {DIRECT_REQUEST_EXPIRY_POLICY_MESSAGES.activePolicy(
            settings.directRequestExpiryPolicy.unansweredTtlDays,
          )}
        </p>
        <form
          onSubmit={(event: SyntheticEvent<HTMLFormElement>) => {
            event.preventDefault();
            const value = new FormData(event.currentTarget).get("unansweredTtlDays");
            void controller.saveRequestExpiryPolicy(
              typeof value === "string" ? Number(value) : Number.NaN,
            );
          }}
        >
          <TextInput
            defaultValue={settings.directRequestExpiryPolicy.unansweredTtlDays}
            disabled={!canManage || busy}
            hint={DIRECT_REQUEST_EXPIRY_POLICY_MESSAGES.settingsHint}
            key={settings.directRequestExpiryPolicy.unansweredTtlDays}
            label={DIRECT_REQUEST_EXPIRY_POLICY_MESSAGES.settingsLabel}
            max={14}
            min={1}
            name="unansweredTtlDays"
            required
            step={1}
            type="number"
          />
          {canManage ? (
            <Button disabled={busy} type="submit" variant="secondary">
              {DIRECT_REQUEST_EXPIRY_POLICY_MESSAGES.settingsAction}
            </Button>
          ) : null}
        </form>
        <Outcome controller={controller} section="requestExpiry" />
      </Card>

      <Card title={CIRCLE_SETTINGS_MESSAGES.timeZone.legend} titleLevel={2}>
        <form
          onSubmit={(event: SyntheticEvent<HTMLFormElement>) => {
            event.preventDefault();
            const value = new FormData(event.currentTarget).get("timeZone");
            // Never saved straight from the form: this is the edit that changes what
            // everybody else reads, so it goes through the explanation first.
            setConfirmingTimeZone(typeof value === "string" ? value : "");
          }}
        >
          <TextInput
            defaultValue={settings.timeZone}
            disabled={!canManage || busy}
            hint={CIRCLE_SETTINGS_MESSAGES.timeZone.hint}
            key={settings.timeZone}
            label={CIRCLE_SETTINGS_MESSAGES.timeZone.label}
            name="timeZone"
          />
          {/* The consequence sits above the control, where it is read before the
              decision rather than after it. */}
          <p className="circle-settings__hint">{CIRCLE_SETTINGS_MESSAGES.timeZone.consequence}</p>
          {canManage ? (
            <Button disabled={busy} type="submit" variant="danger">
              {CIRCLE_SETTINGS_MESSAGES.timeZone.action}
            </Button>
          ) : null}
        </form>
        <Outcome controller={controller} section="timeZone" />
      </Card>

      {settings.supportedPerson === null ? null : (
        <Card title={CIRCLE_SETTINGS_MESSAGES.supportedPerson.legend} titleLevel={2}>
          {settings.supportedPerson.isPlaceholder ? (
            // The D-091 gate, said out loud rather than left in a bead.
            <StatusMessage tone="warning">
              {CIRCLE_SETTINGS_MESSAGES.supportedPerson.placeholderNotice}
            </StatusMessage>
          ) : null}

          {canManage ? (
            <form
              onSubmit={(event: SyntheticEvent<HTMLFormElement>) => {
                event.preventDefault();
                const value = new FormData(event.currentTarget).get("supportedPersonName");
                void controller.saveSupportedPersonName(typeof value === "string" ? value : "");
              }}
            >
              <TextInput
                defaultValue={settings.supportedPerson.chosenName}
                disabled={busy}
                hint={SUPPORTED_PERSON_NAME_MESSAGES.hint}
                key={settings.supportedPerson.chosenName}
                label={SUPPORTED_PERSON_NAME_MESSAGES.label}
                name="supportedPersonName"
              />
              <Button disabled={busy} type="submit" variant="secondary">
                {SUPPORTED_PERSON_NAME_MESSAGES.action}
              </Button>
            </form>
          ) : (
            <p className="circle-settings__name">
              {CIRCLE_SETTINGS_MESSAGES.supportedPerson.nameLabel}:{" "}
              {settings.supportedPerson.chosenName}
            </p>
          )}
          <Outcome controller={controller} section="supportedPersonName" />

          <form
            onSubmit={(event: SyntheticEvent<HTMLFormElement>) => {
              event.preventDefault();
              const value = new FormData(event.currentTarget).get("printContactPersonName");
              void controller.savePrintContactName(typeof value === "string" ? value : "");
            }}
          >
            <TextInput
              defaultValue={settings.supportedPerson.printContactPersonName ?? ""}
              disabled={!canManage || busy}
              hint={SUPPORTED_PERSON_PRINT_CONTACT_MESSAGES.settingsHint}
              key={settings.supportedPerson.printContactPersonName ?? ""}
              label={SUPPORTED_PERSON_PRINT_CONTACT_MESSAGES.settingsLabel}
              maxLength={120}
              name="printContactPersonName"
            />
            {canManage ? (
              <Button disabled={busy} type="submit" variant="secondary">
                {SUPPORTED_PERSON_PRINT_CONTACT_MESSAGES.settingsAction}
              </Button>
            ) : null}
          </form>
          <Outcome controller={controller} section="printContactPerson" />

          <form
            onSubmit={(event: SyntheticEvent<HTMLFormElement>) => {
              event.preventDefault();
              const value = new FormData(event.currentTarget).get("printContactText");
              void controller.savePrintContact(typeof value === "string" ? value : "");
            }}
          >
            <TextInput
              defaultValue={settings.supportedPerson.printContactText ?? ""}
              disabled={!canManage || busy}
              hint={CIRCLE_SETTINGS_MESSAGES.supportedPerson.printContactHint}
              key={settings.supportedPerson.printContactText ?? ""}
              label={CIRCLE_SETTINGS_MESSAGES.supportedPerson.printContactLabel}
              name="printContactText"
            />
            {canManage ? (
              <Button disabled={busy} type="submit" variant="secondary">
                {CIRCLE_SETTINGS_MESSAGES.supportedPerson.action}
              </Button>
            ) : null}
          </form>
          <Outcome controller={controller} section="supportedPerson" />
        </Card>
      )}

      <Dialog
        footer={
          <>
            <Button
              disabled={busy}
              onClick={() => {
                setConfirmingTimeZone(null);
              }}
              variant="secondary"
            >
              {CIRCLE_SETTINGS_MESSAGES.timeZone.cancel}
            </Button>
            <Button
              disabled={busy}
              onClick={() => {
                const zone = confirmingTimeZone ?? "";
                setConfirmingTimeZone(null);
                void controller.saveTimeZone(zone);
              }}
              variant="danger"
            >
              {CIRCLE_SETTINGS_MESSAGES.timeZone.confirmAction}
            </Button>
          </>
        }
        onClose={() => {
          setConfirmingTimeZone(null);
        }}
        open={confirmingTimeZone !== null}
        title={CIRCLE_SETTINGS_MESSAGES.timeZone.confirmTitle}
      >
        <p>{CIRCLE_SETTINGS_MESSAGES.timeZone.confirmBody}</p>
      </Dialog>
    </section>
  );
}

/**
 * The organiser's masked admission list.
 *
 * This component never receives the stored identifier, only the server-owned mask.
 * Whether it renders at all is also server-derived: SettingsRoute mounts it only when
 * the circle read model says the caller has plan.manage.
 */
export function AdmissionAllowlist({
  entries,
  outcome,
  actionsDisabled = false,
  onAdd,
  onRemove,
  headingLevel = 1,
}: AdmissionAllowlistProps) {
  const busy = actionsDisabled || outcome.status === "working";
  const Heading = headingLevel === 1 ? "h1" : "h2";

  return (
    <section data-admission-allowlist>
      <Heading>{ADMISSION_ALLOWLIST_MESSAGES.heading}</Heading>
      <p>{ADMISSION_ALLOWLIST_MESSAGES.explanation}</p>
      <Card title={ADMISSION_ALLOWLIST_MESSAGES.listTitle}>
        <form
          onSubmit={(event: SyntheticEvent<HTMLFormElement>) => {
            event.preventDefault();
            const form = event.currentTarget;
            const value = new FormData(form).get("admissionEmail");
            form.reset();
            void onAdd(typeof value === "string" ? value : "");
          }}
        >
          <TextInput
            disabled={busy}
            hint={ADMISSION_ALLOWLIST_MESSAGES.emailHint}
            label={ADMISSION_ALLOWLIST_MESSAGES.emailLabel}
            name="admissionEmail"
            required
            type="email"
          />
          <Button disabled={busy} type="submit" variant="secondary">
            {ADMISSION_ALLOWLIST_MESSAGES.addAction}
          </Button>
        </form>

        {entries.length === 0 ? (
          <p>{ADMISSION_ALLOWLIST_MESSAGES.empty}</p>
        ) : (
          <ul>
            {entries.map((entry) => (
              <li key={entry.allowlistEntryId}>
                <span>{entry.maskedIdentifier}</span>{" "}
                <Button
                  disabled={busy}
                  onClick={() => {
                    void onRemove(entry.allowlistEntryId);
                  }}
                  variant="secondary"
                >
                  {ADMISSION_ALLOWLIST_MESSAGES.removeAction}
                </Button>
              </li>
            ))}
          </ul>
        )}

        <AdmissionAllowlistStatus outcome={outcome} />
      </Card>
    </section>
  );
}

function AdmissionAllowlistStatus({
  outcome,
}: {
  readonly outcome: AdmissionAllowlistOutcome;
}): ReactNode {
  if (outcome.status === "idle" || outcome.status === "working") return null;
  const message = ADMISSION_ALLOWLIST_MESSAGES[outcome.status];
  return (
    <div data-admission-outcome={outcome.status}>
      <StatusMessage tone={outcome.status === "added" ? "success" : "info"}>
        {message}
      </StatusMessage>
    </div>
  );
}

/**
 * What happened to THIS section.
 *
 * `tone="info"` even for refusals: nothing on a settings screen is an emergency, and
 * `tone="error"` sets `role="alert"`, which would cut across a screen reader to
 * announce that a preference did not save.
 */
function Outcome({
  controller,
  section,
}: {
  readonly controller: CircleSettingsController;
  readonly section: SettingsSection;
}): ReactNode {
  const { outcome } = controller;
  if (outcome.status === "idle" || outcome.status === "working") return null;
  if (outcome.section !== section) return null;

  if (outcome.status === "saved") {
    return (
      <div data-settings-outcome={`${section}:saved`}>
        <StatusMessage tone="success">{CIRCLE_SETTINGS_MESSAGES.saved}</StatusMessage>
      </div>
    );
  }

  if (outcome.status === "invalid") {
    return (
      <div data-settings-outcome={`${section}:invalid`}>
        <StatusMessage tone="info">
          <ul className="circle-settings__problems">
            {blockProblemMessages(outcome.issues).map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </StatusMessage>
      </div>
    );
  }

  const message = messageFor(outcome.rejection);
  return (
    <div data-settings-outcome={`${section}:refused`}>
      <StatusMessage tone="info">
        <div className="circle-settings__lines">
          <p className="circle-settings__problem-title">{message.title}</p>
          <p>{message.body}</p>
        </div>
      </StatusMessage>
    </div>
  );
}

/** Read the nine block fields back out of the form, in the shape the server takes. */
function readBlocks(data: FormData): BlockDefinitionsDraft {
  const field = (name: string) => {
    const value = data.get(name);
    return typeof value === "string" ? value : "";
  };

  const bounds = (blockId: (typeof BLOCK_ORDER)[number]) => ({
    startLocal: field(`${blockId}.startLocal`),
    endLocal: field(`${blockId}.endLocal`),
  });

  return {
    morning: bounds("morning"),
    afternoon: bounds("afternoon"),
    evening: bounds("evening"),
  };
}
